import React, { useState, useRef, useEffect } from 'react';
import {
  Menu,
  Bell,
  User,
  LogOut,
  Settings,
  ChevronDown,
  CheckCircle,
  AlertCircle,
  Shield,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { logout } from '@/firebase/auth';
import toast from 'react-hot-toast';
import NotificationCenter from './NotificationCenter';
import GlobalSearch from './GlobalSearch';
import {
  subscribeToNotifications,
  ensureRecentActivityNotifications,
  canUserSeeNotification,
} from '@/services/notificationService';
import type { Notification } from '@/types';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { appUser, firebaseUser, role, can, isAdmin, companyId, canAny } = useAuthContext();
  const navigate = useNavigate();
  const [showNotif, setShowNotif] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const profileRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Subscribe to real-time notifications for badge
  useEffect(() => {
    if (!companyId) return;
    ensureRecentActivityNotifications(companyId);
    const unsubscribe = subscribeToNotifications(companyId, (items) => {
      setNotifications(items);
    });
    return () => unsubscribe();
  }, [companyId]);

  // Compute unread count only for notifications this role/user is allowed to see
  const unreadCount = notifications
    .filter((n) => canUserSeeNotification(n, { uid: appUser?.uid, isAdmin, can, canAny }))
    .filter((n) => !n.read).length;

  // Close dropdowns on click outside or Escape key
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (notifRef.current && !notifRef.current.contains(target)) {
        setShowNotif(false);
      }
      if (profileRef.current && !profileRef.current.contains(target)) {
        setShowProfile(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowNotif(false);
        setShowProfile(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      toast.success('Logged out successfully');
    } catch {
      toast.error('Failed to logout');
    }
  };

  const userEmail = firebaseUser?.email || appUser?.email || '';
  const emailName = userEmail
    ? userEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

  const userName = (() => {
    if (appUser?.name && appUser.name.trim() && appUser.name.toLowerCase() !== 'admin') {
      return appUser.name.trim();
    }
    if (firebaseUser?.displayName && firebaseUser.displayName.trim() && firebaseUser.displayName.toLowerCase() !== 'admin') {
      return firebaseUser.displayName.trim();
    }
    if (appUser?.name && appUser.name.trim()) {
      return appUser.name.trim();
    }
    if (firebaseUser?.displayName && firebaseUser.displayName.trim()) {
      return firebaseUser.displayName.trim();
    }
    if (emailName) {
      return emailName;
    }
    return 'User';
  })();
  const initial = userName.charAt(0).toUpperCase();
  const roleName = role?.name || appUser?.roleId || 'Staff';

  const photoUrl = appUser?.photoUrl || firebaseUser?.photoURL;

  return (
    <header
      id="header"
      className="h-14 bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-6 flex-shrink-0 z-30"
    >
      {/* Left */}
      <div className="flex items-center gap-3">
        <button
          id="sidebar-toggle"
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        {/* Search (desktop) */}
        <div className="hidden md:block">
          <GlobalSearch />
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            id="notification-btn"
            onClick={() => {
              setShowNotif((p) => !p);
              setShowProfile(false);
            }}
            className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            aria-label="Notifications"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="notif-dot">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {showNotif && <NotificationCenter onClose={() => setShowNotif(false)} />}
        </div>

        {/* Profile Menu Dropdown */}
        <div className="relative" ref={profileRef}>
          <button
            id="profile-menu-btn"
            onClick={() => {
              setShowProfile((p) => !p);
              setShowNotif(false);
            }}
            className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-sm shadow-xs overflow-hidden">
              {photoUrl ? (
                <img src={photoUrl} alt={userName} className="w-full h-full object-cover" />
              ) : (
                initial
              )}
            </div>
            <div className="hidden sm:flex flex-col text-left">
              <span className="text-xs font-bold text-gray-800 leading-tight">{userName}</span>
              <span className="text-[10px] font-semibold text-primary-600 flex items-center gap-0.5">
                <Shield size={9} /> {roleName}
              </span>
            </div>
            <ChevronDown size={14} className="text-gray-400 ml-0.5" />
          </button>

          {showProfile && (
            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-card-lg p-2 z-50 animate-fade-in">
              {/* Profile Card Header */}
              <div className="p-3 border-b border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-base shadow-sm flex-shrink-0 overflow-hidden">
                  {photoUrl ? (
                    <img src={photoUrl} alt={userName} className="w-full h-full object-cover" />
                  ) : (
                    initial
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-800 truncate">{userName}</p>
                  <p className="text-xs text-gray-400 truncate">{userEmail}</p>

                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {/* Role badge */}
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary-700 bg-primary-50 border border-primary-200/80 px-2 py-0.5 rounded-md uppercase tracking-wider">
                      <Shield size={10} /> {roleName}
                    </span>

                    {/* Email Verified */}
                    {firebaseUser?.emailVerified ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200/60 px-1.5 py-0.5 rounded">
                        <CheckCircle size={9} /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200/60 px-1.5 py-0.5 rounded">
                        <AlertCircle size={9} /> Unverified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Menu Links */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowProfile(false);
                    navigate('/settings/profile');
                  }}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-xs font-medium text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-xl transition-colors cursor-pointer"
                >
                  <User size={15} />
                  Profile Details &amp; Role
                </button>

                {(isAdmin || can('Settings.Manage')) && (
                  <button
                    onClick={() => {
                      setShowProfile(false);
                      navigate('/settings/company');
                    }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-xs font-medium text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-xl transition-colors cursor-pointer"
                  >
                    <Settings size={15} />
                    Company Settings
                  </button>
                )}
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 pt-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-xs font-semibold text-danger-600 hover:bg-danger-50 rounded-xl transition-colors cursor-pointer"
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
